import { 
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    Box,
    Grid2,
    Icon
} from "@mui/material";
import { useEffect, useState } from "react";
import { ValidatorForm } from "react-material-ui-form-validator";
import { useParams } from "react-router-dom";
import { StandardButton } from "../../../_components/button/primary.button";
import { StandardTextValidator } from "../../../_components/textValidator/standardTextValidator";
import { toastr } from "../../../_components/toastr/toastr";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { clientContactService } from '../../../_services/clientContact.service';

export function ClientContactManage(params?: { clientId?: string | number; onSaved?(): void }) {

    const resetData = () => ({ name: ''});

    const { id } = useParams<{ id: string }>();
    const resolvedClientId = (params?.clientId ?? id)?.toString();

    const [data, setData] = useState<any>(resetData);
    const [isLoading, setIsLoading] = useState(false);
    const [saving, setIsSaving] = useState(false);
    const [contacts, setContacts] = useState<any[]>([]);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [showForm, setShowForm] = useState(false);

    useEffect(() => {
        if (resolvedClientId != null) fetchData();
        else setData(resetData);
    }, [resolvedClientId]);

    function fetchData() {
        if (!resolvedClientId) return;
        setIsLoading(true);
        return clientContactService
            .getAll(resolvedClientId)
            .then(
                (json) => {
                    setContacts(json);
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);

        const _data = { ...data, clientId: resolvedClientId };
        if (_data.address && !Object.values(_data.address).some((value) => value != null && String(value).trim() !== "")) {
            delete _data.address;
        }

        return clientContactService
            .submit(_data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    setEditingIndex(null);
                    setData(resetData());
                    setShowForm(false);
                    fetchData();
                    params?.onSaved?.();
                },
                handleErrorResponse
            )
            .finally(() => setIsSaving(false));
    }

    function disable(id: string) {
        if (!id) return;
        setIsLoading(true);
        return clientContactService
            .disable(id)
            .then(
                () => {
                    toastr.success("Disabled successfully");
                    setData(resetData());
                    fetchData();
                },
                handleErrorResponse
            )
            .finally(() => setIsLoading(false));
    }

    const edit = (index: number) => {
        const contact = contacts[index];
        setData(contact);
        setEditingIndex(index);
        setShowForm(true);
    };

    const addNew = () => {
        setData(resetData());
        setEditingIndex(null);
        setShowForm(true);
    };

    const cancel = () => {
        setShowForm(false);
        setData(resetData());
        setEditingIndex(null);
    };

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }));
    }

    const handleAddressChange = (event: any) => {
        setData((_data: any) => ({
            ..._data, address:
            {
                ..._data.address, [event.target.name]: event.target.value
            }
        }));
    };

    return (
        <Box p={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6"></Typography>
                {!showForm && (
                <Button variant="contained" onClick={addNew}>
                    Add Contact
                    </Button>
                )}
            </Box>

            {showForm && (
                <ValidatorForm onSubmit={submit} noValidate>
                    <Grid2 container spacing={2}>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Grid2 container spacing={1.5}>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        name="name"
                                        label="Name"
                                        required
                                        validators={['required', 'minStringLength:1']}
                                        errorMessages={['required field', "Field cannot be empty"]}
                                        fullWidth
                                        value={data.name}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        name="jobTitle"
                                        label="Job Title"
                                        fullWidth
                                        value={data.jobTitle}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        name="phone"
                                        label="Phone"
                                        fullWidth
                                        validators={['matchRegexp:^\\+?[0-9]{7,15}$']}
                                        errorMessages={['Enter a valid phone number (7-15 digits, optional +)']}
                                        value={data.phone}
                                        onChange={handleChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        name="email"
                                        label="Email"
                                        fullWidth
                                        required
                                        validators={['required', 'minStringLength:1', 'isEmail']}
                                        errorMessages={['required field', "Field cannot be empty",'Enter a valid email address']}
                                        value={data.email}
                                        onChange={handleChange}
                                    />
                                </Grid2>
                            </Grid2>
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 6 }}>
                            <Grid2 container spacing={1.5}>
                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="addressLine1"
                                        name="addressLine1"
                                        label="Address Line 1"
                                        fullWidth
                                        value={ data?.address?.addressLine1}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="addressLine2"
                                        name="addressLine2"
                                        label="Address Line 2"
                                        fullWidth
                                        value={ data?.address?.addressLine2}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="city"
                                        name="city"
                                        label="Town/City"
                                        fullWidth
                                        value={ data?.address?.city}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12 }}>
                                    <StandardTextValidator
                                        key="eirCode"
                                        name="eirCode"
                                        label="EirCode/PostCode"
                                        fullWidth
                                        value={ data?.address?.eirCode}
                                        onChange={handleAddressChange}
                                    />
                                </Grid2>
                            </Grid2>
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                key="country"
                                name="country"
                                label="Country"
                                fullWidth
                                value={ data?.address?.country}
                                onChange={handleAddressChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 12 }}>
                            <StandardTextValidator
                                name="notes"
                                label="Notes"
                                fullWidth
                                multiline
                                rows={3}
                                value={data.notes}
                                onChange={handleChange}
                            />
                        </Grid2>

                        <Grid2 size={{ xs: 6 }}>
                            <StandardButton
                                saving={saving}
                                loading={isLoading}
                                icon="save"
                                type="submit"
                            >
                                Save
                            </StandardButton>
                        </Grid2>
                        <Grid2 size={{ xs: 6 }}>
                            <StandardButton
                                saving={saving}
                                loading={isLoading}
                                icon="block"
                                variant="error"
                                onClick={cancel}>
                                Cancel
                            </StandardButton>
                        </Grid2>
                    </Grid2>
                </ValidatorForm>
            )}

            <Box mt={4}>
                <Grid2 container spacing={2} mt={1}>
                    {contacts.map((contact, index) => (
                        <Grid2 size={{ xs: 12, sm: 6 }} key={index}>
                            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }} >
                                <CardContent>
                                    <Typography variant="subtitle1">{contact.name}</Typography>
                                    <Typography color="textSecondary">{contact.jobTitle}</Typography>
                                    <Typography color="textSecondary">{contact.phone}</Typography>
                                    <Typography color="textSecondary">{contact.email}</Typography>
                                    <Box mt={1}>
                                        <IconButton onClick={() => edit(index)} color="primary">
                                            <Icon>edit</Icon>
                                        </IconButton>
                                        <IconButton onClick={() => disable(contact.id)} color="error">
                                            <Icon>delete</Icon>
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid2>
                    ))}
                </Grid2>
            </Box>
        </Box>
    );
}
